import { useState } from 'react';
import type { Model, CameraRef } from '../../api';
import Modal from '../common/Modal';
import { BTN_PRIMARY, BTN_SECONDARY, LABEL, SELECT, TEXTAREA } from '../common/ui';

interface ModelAttachModalProps {
  model: Model | null;
  cameras: CameraRef[];
  onAttach: (modelId: string, cameraId: string, config: unknown) => Promise<void> | void;
  onClose: () => void;
}

export default function ModelAttachModal({ model, cameras, onAttach, onClose }: ModelAttachModalProps) {
  const [selectedCamera, setSelectedCamera] = useState('');
  const [config, setConfig] = useState('{}');
  const [loading, setLoading] = useState(false);

  if (!model) return null;

  async function handleAttach() {
    if (!selectedCamera || !model) return;
    setLoading(true);
    try {
      let parsedConfig: unknown = {};
      try { parsedConfig = JSON.parse(config); } catch { /* use empty */ }
      await onAttach(model.id, selectedCamera, parsedConfig);
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Attach "${model.name}" to Camera`}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={BTN_SECONDARY}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAttach}
            disabled={loading || !selectedCamera}
            className={BTN_PRIMARY}
          >
            {loading ? 'Attaching…' : 'Attach'}
          </button>
        </div>
      }
    >
      <div className="space-y-4 p-5">
        <div>
          <label className={LABEL}>Select Camera *</label>
          <select
            value={selectedCamera}
            onChange={(e) => setSelectedCamera(e.target.value)}
            className={SELECT}
          >
            <option value="">Choose a camera</option>
            {(cameras || []).map((cam) => (
              <option key={cam.id} value={cam.id}>{cam.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={LABEL}>Config (JSON)</label>
          <textarea
            value={config}
            onChange={(e) => setConfig(e.target.value)}
            rows={4}
            className={`${TEXTAREA} font-mono`}
          />
        </div>
      </div>
    </Modal>
  );
}
